import { RadioGroup as RadioGroupPrimitive } from 'radix-ui';
import * as React from 'react';

// Type-only import so the `{@link Checkbox}` JSDoc reference below resolves.
// eslint-disable-next-line unused-imports/no-unused-imports, @typescript-eslint/no-unused-vars
import { type Checkbox } from './Checkbox';
import { cn } from './utils/cn';

/**
 * Group of mutually-exclusive options. Use when exactly one value from a
 * small known set must be picked.
 *
 * For multi-select inputs (zero or more options), use {@link Checkbox} per
 * option instead.
 *
 * @example
 * <RadioGroup defaultValue="md" aria-label="Note size">
 *   <Stack direction="horizontal" align="center" gap={2}>
 *     <RadioGroupItem id="size-sm" value="sm" />
 *     <label htmlFor="size-sm">Small</label>
 *   </Stack>
 *   <Stack direction="horizontal" align="center" gap={2}>
 *     <RadioGroupItem id="size-md" value="md" />
 *     <label htmlFor="size-md">Medium</label>
 *   </Stack>
 * </RadioGroup>
 */
function RadioGroup({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Root>) {
  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-group"
      className={cn('grid w-full gap-2', className)}
      {...props}
    />
  );
}

/** A single option inside a {@link RadioGroup}. Pair with a `<label>` for accessibility. */
function RadioGroupItem({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item>) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      className={cn(
        'group/radio-group-item peer relative flex aspect-square size-4 shrink-0 rounded-full border border-border-tertiary outline-none after:absolute after:-inset-x-3 after:-inset-y-2 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:bg-input/30 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 data-checked:border-primary data-checked:bg-primary data-checked:text-primary-foreground aria-invalid:data-checked:border-destructive aria-invalid:data-checked:bg-destructive dark:data-checked:bg-primary',
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="flex size-4 items-center justify-center"
      >
        <span className="absolute top-1/2 left-1/2 size-2 -translate-1/2 rounded-full bg-primary-foreground" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
}

export { RadioGroup, RadioGroupItem };
